import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from "react-router-dom";
import "./App.css";
import Layout from "./Components/Layout";
import ForgotPassword from "./Components/ForgotPassword";
import Login from "./Components/Login";
import Registration from "./Components/Registration";
import ResetPassword from "./Components/ResetPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AllImages from "./Designs/AllImages";
import Canvas from "./Designs/Canvas";
import Dashboard from "./Designs/Dashboard";
import Favorites from "./Designs/Favorites";
import GalleryWall from "./Designs/GalleryWall";
import Murals from "./Designs/Murals";
import Wallpaper from "./Designs/Wallpaper";
import SyncLogs from "./Admin/SyncLogs";
import SyncStatus from "./Admin/SyncStatus";
import Users from "./Admin/Users";
import ProductDetails from "./Admin/ProductDetails";
import CompanySettings from "./Quotations/CompanySettings";
import QuotationEditor from "./Quotations/QuotationEditor";
import QuotationList from "./Quotations/QuotationList";
import Calculator from "./Architect/Calculator";
import ProjectDetail from "./Architect/ProjectDetail";
import ProjectList from "./Architect/ProjectList";
import WallEditor from "./Architect/WallEditor";
import WallList from "./Architect/WallList";
import MoodboardEditor from "./Moodboard/MoodboardEditor";
import MoodboardList from "./Moodboard/MoodboardList";
import SharedMoodboard from "./Moodboard/SharedMoodboard";

/**
 * Routing.
 *
 * Routes are always declared and the guards below decide what renders. Making
 * the protected tree itself conditional on a logged-in flag would bounce a
 * page refresh to /login before the session finished restoring.
 */

function FullPageMessage({ children }) {
  return <div className="app-loading">{children}</div>;
}

/** Blocks until the session is resolved, then requires a signed-in user. */
function RequireAuth() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullPageMessage>Loading...</FullPageMessage>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

/** Admin-only section. The sidebar hides these, this enforces it. */
function RequireAdmin() {
  const { isAdmin, loading } = useAuth();

  if (loading) return <FullPageMessage>Loading...</FullPageMessage>;
  if (!isAdmin) return <Navigate to="/Dashboard" replace />;
  return <Outlet />;
}

/** Keeps signed-in users away from the login and registration screens. */
function PublicOnly({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullPageMessage>Loading...</FullPageMessage>;
  if (isAuthenticated) return <Navigate to="/Dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnly>
            <Login />
          </PublicOnly>
        }
      />
      <Route
        path="/registration"
        element={
          <PublicOnly>
            <Registration />
          </PublicOnly>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicOnly>
            <ForgotPassword />
          </PublicOnly>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/shared/moodboards/:token" element={<SharedMoodboard />} />

      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route path="/Dashboard" element={<Dashboard />} />
          <Route path="/All-Images" element={<AllImages />} />
          <Route path="/Wallpaper" element={<Wallpaper />} />
          <Route path="/Canvas" element={<Canvas />} />
          <Route path="/Gallery-Walls" element={<GalleryWall />} />
          <Route path="/Murals" element={<Murals />} />
          <Route path="/Favorites" element={<Favorites />} />
          <Route path="/moodboards" element={<MoodboardList />} />
          <Route path="/moodboards/:boardId" element={<MoodboardEditor />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/visualiser" element={<WallList />} />
          <Route path="/visualiser/:visId" element={<WallEditor />} />
          <Route path="/calculator" element={<Calculator />} />

          <Route element={<RequireAdmin />}>
            <Route path="/admin/users" element={<Users />} />
            <Route path="/admin/sync-status" element={<SyncStatus />} />
            <Route path="/admin/sync-logs" element={<SyncLogs />} />
            <Route path="/admin/products" element={<ProductDetails />} />
            <Route path="/admin/quotations" element={<QuotationList />} />
            <Route path="/admin/quotations/:quoteId" element={<QuotationEditor />} />
            <Route path="/admin/company" element={<CompanySettings />} />
          </Route>
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/Dashboard" replace />} />
      <Route path="*" element={<Navigate to="/Dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}
